import { COPE_STRATEGIES, COPE_T } from "@/data/cope";

const L = (lang, fr, es) => (lang === "es" ? es : fr);

export function levelOf(score) {
  if (score <= 2) return "faible";
  if (score <= 4) return "modere";
  return "eleve";
}

// answers: { "1": 0..3, ... } -> rows par stratégie
export function computeCope(answers, lang = "fr") {
  const rows = COPE_STRATEGIES.map((s) => {
    const a = Number(answers[s.items[0]]) || 0;
    const b = Number(answers[s.items[1]]) || 0;
    const score = a + b; // 0..6
    const level = levelOf(score);
    return {
      key: s.key,
      name: s.lang ? s[lang] : s[lang],
      category: s.category,
      score,
      pct: Math.round((score / 6) * 100),
      level,
      levelLabel: COPE_T[lang].levels[level],
    };
  });
  const ranked = [...rows].sort((a, b) => b.score - a.score);
  return { rows, ranked };
}

const byKey = (rows) => Object.fromEntries(rows.map((r) => [r.key, r]));

const listNames = (rows, lang) => {
  const names = rows.map((r) => r.name);
  if (names.length <= 1) return names[0] || "";
  const sep = L(lang, " et ", " y ");
  return names.slice(0, -1).join(", ") + sep + names[names.length - 1];
};

// ---- Ressources psychologiques potentielles (point 5) ----
function resources(m, lang) {
  const out = [];
  const avg = (...ks) => ks.reduce((s, k) => s + m[k].score, 0) / ks.length;
  if (avg("afrontamiento_activo", "planificacion") >= 3)
    out.push(L(lang, "Résolution de problème : capacité à analyser la situation et à engager des actions concrètes.", "Resolución de problemas: capacidad para analizar la situación y emprender acciones concretas."));
  if (avg("reevaluacion_positiva", "humor") >= 3)
    out.push(L(lang, "Flexibilité : aptitude à changer de point de vue et à dédramatiser.", "Flexibilidad: capacidad para cambiar de perspectiva y quitar dramatismo."));
  if (avg("apoyo_emocional", "apoyo_social") >= 3)
    out.push(L(lang, "Soutien social : recours possible à l'entourage pour du réconfort et de l'aide concrète.", "Apoyo social: posibilidad de recurrir al entorno para consuelo y ayuda concreta."));
  if (avg("reevaluacion_positiva", "aceptacion") >= 3)
    out.push(L(lang, "Régulation émotionnelle : gestion des émotions par recadrage et acceptation.", "Regulación emocional: manejo de las emociones mediante reencuadre y aceptación."));
  if (m.aceptacion.score >= 3)
    out.push(L(lang, "Acceptation : capacité à composer avec ce qui ne peut être changé.", "Aceptación: capacidad para convivir con lo que no se puede cambiar."));
  if (m.reevaluacion_positiva.score >= 3)
    out.push(L(lang, "Optimisme : tendance à rechercher le positif dans l'adversité.", "Optimismo: tendencia a buscar lo positivo en la adversidad."));
  return out;
}

// ---- Vulnérabilités potentielles (point 6) ----
function vulnerabilities(m, lang) {
  const out = [];
  const avg = (...ks) => ks.reduce((s, k) => s + m[k].score, 0) / ks.length;
  if (avg("autodistraccion", "desconexion") >= 3)
    out.push(L(lang, "Évitement : tendance à détourner l'attention plutôt qu'à affronter le problème.", "Evitación: tendencia a desviar la atención en lugar de afrontar el problema."));
  if (m.autoinculpacion.score >= 3)
    out.push(L(lang, "Autodépréciation : tendance à se rendre responsable et à se critiquer.", "Autodesvalorización: tendencia a culparse y criticarse."));
  if (m.negacion.score >= 3)
    out.push(L(lang, "Déni : difficulté à reconnaître la réalité de la situation.", "Negación: dificultad para reconocer la realidad de la situación."));
  if (avg("apoyo_emocional", "apoyo_social") <= 2)
    out.push(L(lang, "Isolement : faible recours au soutien de l'entourage.", "Aislamiento: escaso recurso al apoyo del entorno."));
  if (m.desconexion.score >= 3)
    out.push(L(lang, "Désengagement : renoncement face aux difficultés.", "Desvinculación: abandono ante las dificultades."));
  if (m.uso_sustancias.score >= 1)
    out.push(L(lang, "Usage de substances : recours à l'alcool ou à d'autres produits pour faire face — point de vigilance.", "Uso de sustancias: recurso al alcohol u otros productos para afrontar — punto de vigilancia."));
  if (m.desahogo.score >= 4)
    out.push(L(lang, "Rumination possible : expression répétée des affects négatifs sans résolution.", "Posible rumiación: expresión repetida de afectos negativos sin resolución."));
  return out;
}

// ---- Style général (point 4) ----
function generalStyle(rows, lang) {
  const catAvg = (cat) => {
    const r = rows.filter((x) => x.category === cat);
    return r.reduce((s, x) => s + x.score, 0) / (r.length || 1);
  };
  const adap = catAvg("adaptative");
  const evit = catAvg("evitante");
  if (adap - evit >= 1)
    return L(lang, "Le style d'adaptation apparaît globalement orienté vers l'action et la mobilisation de ressources : la personne tend à affronter les difficultés plutôt qu'à les éviter.", "El estilo de afrontamiento aparece globalmente orientado a la acción y a la movilización de recursos: la persona tiende a afrontar las dificultades más que a evitarlas.");
  if (evit - adap >= 1)
    return L(lang, "Le style d'adaptation apparaît plutôt orienté vers l'évitement : la personne tend à se détourner des difficultés ou à en différer la confrontation.", "El estilo de afrontamiento aparece más orientado a la evitación: la persona tiende a apartarse de las dificultades o a posponer su confrontación.");
  return L(lang, "Le style d'adaptation apparaît mixte : stratégies actives et stratégies d'évitement coexistent selon les situations.", "El estilo de afrontamiento aparece mixto: coexisten estrategias activas y estrategias de evitación según las situaciones.");
}

// ---- Conséquences possibles (point 7) ----
function consequences(m, lang) {
  const evit = (m.autodistraccion.score + m.desconexion.score) / 2 >= 3 || m.negacion.score >= 3;
  const lowSupport = (m.apoyo_emocional.score + m.apoyo_social.score) / 2 <= 2;
  const active = (m.afrontamiento_activo.score + m.planificacion.score) / 2 >= 3;
  return {
    work: evit
      ? L(lang, "Travail : risque de procrastination ou de désengagement face aux tâches perçues comme difficiles.", "Trabajo: riesgo de procrastinación o desvinculación ante tareas percibidas como difíciles.")
      : L(lang, "Travail : capacité à organiser et à mener des actions ciblées face aux contraintes.", "Trabajo: capacidad para organizar y llevar a cabo acciones dirigidas ante las exigencias."),
    relations: lowSupport
      ? L(lang, "Relations : tendance à gérer seul(e), avec un moindre partage émotionnel.", "Relaciones: tendencia a gestionar en solitario, con menor apertura emocional.")
      : L(lang, "Relations : appui possible sur l'entourage, favorisant le partage et le soutien mutuel.", "Relaciones: apoyo posible en el entorno, que favorece el intercambio y el sostén mutuo."),
    emotion:
      m.reevaluacion_positiva.score >= 3 || m.aceptacion.score >= 3
        ? L(lang, "Gestion émotionnelle : ressources de recadrage et d'acceptation qui facilitent l'apaisement.", "Manejo emocional: recursos de reencuadre y aceptación que facilitan la calma.")
        : L(lang, "Gestion émotionnelle : régulation plus fragile, les affects négatifs pouvant persister.", "Manejo emocional: regulación más frágil, los afectos negativos pueden persistir."),
    crisis: active
      ? L(lang, "Situations de crise : réactivité et recherche de solutions concrètes attendues.", "Situaciones de crisis: se espera reactividad y búsqueda de soluciones concretas.")
      : L(lang, "Situations de crise : risque de sidération ou d'évitement, à soutenir par un cadre structurant.", "Situaciones de crisis: riesgo de bloqueo o evitación, a sostener con un marco estructurante."),
  };
}

// ---- Recommandations thérapeutiques (point 8) ----
function recommendations(m, lang) {
  const rec = new Set();
  const add = (x) => rec.add(x);
  if (m.negacion.score >= 3 || (m.autodistraccion.score + m.desconexion.score) / 2 >= 3)
    add(L(lang, "TCC : restructuration cognitive et exposition progressive pour réduire l'évitement.", "TCC: reestructuración cognitiva y exposición gradual para reducir la evitación."));
  if (m.autoinculpacion.score >= 3)
    add(L(lang, "Thérapie des schémas : travail sur les schémas d'auto-critique et d'exigence.", "Terapia de esquemas: trabajo sobre los esquemas de autocrítica y exigencia."));
  if (m.aceptacion.score <= 2)
    add(L(lang, "ACT : développement de l'acceptation et de l'engagement vers les valeurs.", "ACT: desarrollo de la aceptación y del compromiso con los valores."));
  if (m.reevaluacion_positiva.score <= 2 || m.desahogo.score >= 4)
    add(L(lang, "Régulation émotionnelle et pleine conscience : outils de recadrage et d'ancrage.", "Regulación emocional y mindfulness: herramientas de reencuadre y anclaje."));
  if ((m.apoyo_emocional.score + m.apoyo_social.score) / 2 <= 2)
    add(L(lang, "Compétences d'affirmation de soi : oser demander de l'aide et exprimer ses besoins.", "Habilidades de asertividad: atreverse a pedir ayuda y expresar las necesidades."));
  if (m.uso_sustancias.score >= 1)
    add(L(lang, "Psychoéducation sur le rôle des substances dans la gestion du stress, orientation si besoin.", "Psicoeducación sobre el papel de las sustancias en la gestión del estrés, derivación si procede."));
  add(L(lang, "Psychoéducation générale sur les stratégies de coping et leurs effets à court et long terme.", "Psicoeducación general sobre las estrategias de afrontamiento y sus efectos a corto y largo plazo."));
  return [...rec];
}

// ---- Contradictions (point 9) ----
function contradictions(m, lang) {
  const out = [];
  if (m.afrontamiento_activo.score >= 4 && m.desconexion.score >= 4)
    out.push(L(lang, "Coping actif et désengagement élevés simultanément : la personne peut alterner entre phases de mobilisation et moments de renoncement, souvent selon l'intensité du stress ou le domaine concerné.", "Afrontamiento activo y desvinculación elevados a la vez: la persona puede alternar entre fases de movilización y momentos de abandono, a menudo según la intensidad del estrés o el ámbito afectado."));
  if (m.aceptacion.score >= 4 && m.negacion.score >= 4)
    out.push(L(lang, "Acceptation et déni élevés en même temps : l'acceptation peut porter sur certains aspects tandis que d'autres restent trop douloureux pour être pleinement reconnus.", "Aceptación y negación elevadas simultáneamente: la aceptación puede referirse a ciertos aspectos mientras otros siguen siendo demasiado dolorosos para ser plenamente reconocidos."));
  if ((m.apoyo_emocional.score + m.apoyo_social.score) / 2 >= 4 && (m.autodistraccion.score + m.desconexion.score) / 2 >= 4)
    out.push(L(lang, "Recherche de soutien et évitement coexistent : la personne peut solliciter les autres tout en évitant le cœur du problème.", "Búsqueda de apoyo y evitación coexisten: la persona puede recurrir a los demás y a la vez evitar el núcleo del problema."));
  return out;
}

// ---- Synthèse clinique (~10 lignes, point 11) ----
function synthesis(rows, ranked, lang) {
  const m = byKey(rows);
  const top = ranked.slice(0, 3);
  const low = [...ranked].reverse().slice(0, 3);
  const style = generalStyle(rows, lang);
  const res = resources(m, lang);
  const vul = vulnerabilities(m, lang);
  const p1 = L(
    lang,
    `Les stratégies les plus mobilisées sont ${listNames(top, lang)}, tandis que ${listNames(low, lang)} sont peu utilisées.`,
    `Las estrategias más movilizadas son ${listNames(top, lang)}, mientras que ${listNames(low, lang)} se utilizan poco.`
  );
  const p3 = res.length
    ? L(lang, `Les ressources repérées (${res.length}) offrent des points d'appui thérapeutiques.`, `Los recursos identificados (${res.length}) ofrecen puntos de apoyo terapéuticos.`)
    : L(lang, "Peu de ressources adaptatives ressortent nettement, ce qui invite à les renforcer.", "Destacan pocos recursos adaptativos, lo que invita a reforzarlos.");
  const p4 = vul.length
    ? L(lang, `Certaines vulnérabilités (${vul.length}) méritent une attention particulière.`, `Algunas vulnerabilidades (${vul.length}) merecen especial atención.`)
    : L(lang, "Aucune vulnérabilité marquée ne ressort du profil.", "No destaca ninguna vulnerabilidad marcada en el perfil.");
  const p5 = L(
    lang,
    "Cette lecture reste indicative : elle éclaire des tendances d'adaptation sans constituer un diagnostic, et doit être replacée dans le contexte de vie de la personne.",
    "Esta lectura es orientativa: aclara tendencias de afrontamiento sin constituir un diagnóstico, y debe situarse en el contexto vital de la persona."
  );
  return [style, p1, p3, p4, p5].join(" ");
}

export function copeReport(answers, lang = "fr", situation = "") {
  const { rows, ranked } = computeCope(answers, lang);
  const m = byKey(rows);
  const top = ranked.slice(0, 3);
  const low = [...ranked].reverse().slice(0, 3);
  const catAvg = (cat) => {
    const r = rows.filter((x) => x.category === cat);
    return Math.round((r.reduce((s, x) => s + x.score, 0) / (r.length || 1)) * 10) / 10;
  };
  const categories = { adaptative: catAvg("adaptative"), ambivalente: catAvg("ambivalente"), evitante: catAvg("evitante") };
  const sit = (situation || "").trim();
  const situationSentence = sit
    ? L(lang, `En référence à la situation décrite (« ${sit} »), `, `En referencia a la situación descrita («${sit}»), `)
    : "";
  const profilCore = L(
    lang,
    `les stratégies principalement utilisées sont ${listNames(top, lang)}. À l'inverse, ${listNames(low, lang)} sont peu mobilisées. ${generalStyle(rows, lang)}`,
    `las estrategias principalmente utilizadas son ${listNames(top, lang)}. Por el contrario, ${listNames(low, lang)} se movilizan poco. ${generalStyle(rows, lang)}`
  );
  const profilText = situationSentence
    ? situationSentence + profilCore
    : (lang === "es"
        ? `Las estrategias principalmente utilizadas son ${listNames(top, lang)}. Por el contrario, ${listNames(low, lang)} se movilizan poco. ${generalStyle(rows, lang)}`
        : `Les stratégies principalement utilisées sont ${listNames(top, lang)}. À l'inverse, ${listNames(low, lang)} sont peu mobilisées. ${generalStyle(rows, lang)}`);
  return {
    rows,
    ranked,
    situation: sit,
    categories,
    profil: profilText,
    resources: resources(m, lang),
    vulnerabilities: vulnerabilities(m, lang),
    consequences: consequences(m, lang),
    recommendations: recommendations(m, lang),
    contradictions: contradictions(m, lang),
    synthesis: synthesis(rows, ranked, lang),
  };
}
